import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, LogOut, BookOpen, Shield, FileCheck, Building2, Users, TrendingUp, Star, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/hooks/useUserRole';
import { useTheme } from '@/hooks/useTheme';

interface NavigationProps {
  isAuthenticated?: boolean;
  onLogout?: () => void;
}

export function Navigation({ isAuthenticated = false, onLogout }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { role, isSuperAdmin, isLocalAdmin, isAdmin, isFacilitator } = useUserRole();
  const { theme, toggleTheme } = useTheme();

  const navigationItems = isAuthenticated
    ? []
    : [
        { name: 'Hem', href: '/', icon: BookOpen },
        { name: 'Om KBT', href: '/about', icon: BookOpen },
        { name: 'Kontakt', href: '/contact', icon: BookOpen },
      ];

  // Build menu items based on role - each role gets their specific items only
  const authenticatedItems = (() => {
    // Super Admin - backdoor access to everything
    if (isSuperAdmin) {
      return [
        { name: 'Programägare', href: '/super-admin', icon: Building2 },
        { name: 'GDPR & Integritet', href: '/gdpr', icon: FileCheck },
      ];
    }

    // Local Admin or Admin - manage facilitators and clients at their center
    if (isLocalAdmin || role === 'admin') {
      return [
        { name: 'Admin', href: '/admin', icon: Shield },
        { name: 'Bedömning', href: '/assessment', icon: Star },
        { name: 'GDPR & Integritet', href: '/gdpr', icon: FileCheck },
      ];
    }

    // Facilitator - dashboard for assigned clients only
    if (role === 'facilitator') {
      return [
        { name: 'Behandlare', href: '/facilitator', icon: Users },
        { name: 'Bedömning', href: '/assessment', icon: Shield },
        { name: 'GDPR & Integritet', href: '/gdpr', icon: FileCheck },
      ];
    }

    // Client - portal, chat, and GDPR
    return [
      { name: 'Portalen', href: '/portal', icon: BookOpen },
      { name: 'Mitt Framsteg', href: '/progress', icon: TrendingUp },
      { name: 'Rekommendationer', href: '/recommendations', icon: Star },
      { name: 'Chatta med Stella', href: '/stella', icon: BookOpen },
      { name: 'GDPR & Integritet', href: '/gdpr', icon: FileCheck },
    ];
  })();

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" aria-label="Huvudnavigering">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to={isAuthenticated ? "/portal" : "/"} className="flex items-center space-x-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground font-bold">
              H
            </div>
            <span className="text-xl font-bold">H.J.Ä.L.P.</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                aria-current={location.pathname === item.href ? 'page' : undefined}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary",
                  location.pathname === item.href
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                {item.name}
              </Link>
            ))}

            {isAuthenticated && authenticatedItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                aria-current={location.pathname === item.href ? 'page' : undefined}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary flex items-center gap-1",
                  location.pathname === item.href
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                {item.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Växla tema">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {isAuthenticated ? (
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logga ut
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button variant="outline" asChild>
                  <Link to="/login">Logga in</Link>
                </Button>
                <Button asChild>
                  <Link to="/register">Registrera</Link>
                </Button>
              </div>
            )}
          </div>

          <div className="flex md:hidden items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Växla tema">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Öppna meny"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t bg-background/95 backdrop-blur">
            <div className="container mx-auto px-4 py-4 space-y-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={location.pathname === item.href ? 'page' : undefined}
                  className={cn(
                    "block text-sm font-medium transition-colors hover:text-primary",
                    location.pathname === item.href
                      ? "text-primary"
                      : "text-muted-foreground"
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {isAuthenticated && authenticatedItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={location.pathname === item.href ? 'page' : undefined}
                  className={cn(
                    "block text-sm font-medium transition-colors hover:text-primary flex items-center gap-2",
                    location.pathname === item.href
                      ? "text-primary"
                      : "text-muted-foreground"
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                  {item.name}
                </Link>
              ))}
              
              <div className="border-t pt-4">
                {isAuthenticated ? (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logga ut
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button variant="outline" asChild className="w-full">
                      <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                        Logga in
                      </Link>
                    </Button>
                    <Button asChild className="w-full">
                      <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                        Registrera
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
